import { useCallback, useEffect, useMemo, useState } from "react";
import type { Agent } from "@/types/aiToolkit";
import type { AgentHandoffSession } from "@/types/agentHandoff";
import type { AgentAnalysisResult } from "@/types/communications";
import {
  appendChatToItemBodies,
  mergeChatIntoClickUpMeetings,
  mergeChatIntoResults,
} from "@/lib/agentHandoffMerge";
import { getHandoffSession } from "@/lib/agentHandoffSession";
import { getAgentById as getBuiltinAgentById } from "@/data/aiToolkit";
import { aiAgentsService } from "@/services/aiAgentsService";
import type { AgentActionType } from "@/lib/agentActionRouter";
import type { ClickUpActionContext, AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import { useCommunicationAnalyze } from "@/hooks/useCommunicationAnalyze";

export interface HandoffRestoreState {
  session: AgentHandoffSession;
  agent: Agent | null;
  items: AnalyzeItemInput[];
  results: AgentAnalysisResult[];
  actionType: AgentActionType;
  clickUpContext: ClickUpActionContext | null;
}

async function resolveAgent(agentId: string): Promise<Agent | null> {
  return getBuiltinAgentById(agentId) ?? (await aiAgentsService.getAgentById(agentId));
}

export function useAgentHandoffResume(resumeHandoffId: string | null) {
  const [restoreState, setRestoreState] = useState<HandoffRestoreState | null>(null);

  useEffect(() => {
    if (!resumeHandoffId) {
      setRestoreState(null);
      return;
    }

    void (async () => {
      const session = getHandoffSession(resumeHandoffId);
      if (!session) return;

      const agent = await resolveAgent(session.agentId);
      const chatRefinements = session.chatRefinements ?? "";

      const items = appendChatToItemBodies(session.items, chatRefinements);
      const results = mergeChatIntoResults(session.results, chatRefinements);

      let clickUpContext: ClickUpActionContext | null = null;
      if (session.actionType === "clickup" && session.clickUpContext && agent) {
        clickUpContext = {
          agent,
          teamId: session.clickUpContext.teamId,
          meetings: mergeChatIntoClickUpMeetings(
            session.clickUpContext.meetings,
            chatRefinements
          ),
        };
      }

      setRestoreState({
        session,
        agent,
        items,
        results,
        actionType: session.actionType,
        clickUpContext,
      });
    })();
  }, [resumeHandoffId]);

  const clearRestore = useCallback(() => setRestoreState(null), []);

  return { restoreState, clearRestore };
}

export function getResumeHandoffIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("resumeHandoff");
}

export function clearResumeHandoffUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("resumeHandoff");
  url.searchParams.delete("autoRerun");
  window.history.replaceState({}, "", `${url.pathname}${url.search}`);
}

export function useIntegrationHandoffResume(
  analyze: ReturnType<typeof useCommunicationAnalyze>
) {
  const resumeHandoffId = useMemo(() => getResumeHandoffIdFromUrl(), []);
  const { restoreState, clearRestore } = useAgentHandoffResume(resumeHandoffId);

  useEffect(() => {
    if (!restoreState) return;

    const params = new URLSearchParams(window.location.search);
    const autoRerun = params.get("autoRerun") === "1";

    clearResumeHandoffUrl();

    if (autoRerun && restoreState.agent) {
      void analyze.runBatchAnalyze(restoreState.agent.id, restoreState.items);
    } else {
      analyze.restoreAnalyzeState({
        results: restoreState.results,
        agent: restoreState.agent,
        actionType: restoreState.actionType,
        clickUpContext: restoreState.clickUpContext,
        items: restoreState.items,
      });
    }

    clearRestore();
  }, [restoreState, analyze, clearRestore]);
}
